'use server'

import { apiRequest, ApiResponse } from '../request'

export type FinancialTransferStatus =
    | 'PENDING_APPROVAL'
    | 'APPROVED'
    | 'REJECTED'
    | 'PROCESSING'
    | 'DONE'
    | 'FAILED'
    | 'CANCELLED'

export type FinancialTransferKind = 'STANDALONE' | 'BILLING_PAYMENT'

export type TransferDestinationType =
    | 'BANK_ACCOUNT'
    | 'PIX'
    | 'PARTNER'
    | 'PROFESSIONAL'
    | 'SUPPLIER'
    | 'ORG_OWN_BANK'

export interface FinancialTransfer {
    id: number
    organizationId: number
    kind: FinancialTransferKind
    billingId: number | null
    destinationType: TransferDestinationType
    bankAccountId: number | null
    partnerId: number | null
    professionalId: number | null
    supplierId: number | null
    pixKey: string | null
    pixKeyType: string | null
    bankCode: string | null
    bankAgency: string | null
    bankAccountNumber: string | null
    amount: number
    description: string | null
    status: FinancialTransferStatus
    requestedById: number
    requestedBy?: { id: number; name: string; email: string } | null
    approvedById: number | null
    approvedBy?: { id: number; name: string; email: string } | null
    approvedAt: string | null
    rejectedReason: string | null
    providerTransferId: string | null
    scheduledFor: string | null
    executedAt: string | null
    failReason: string | null
    createdAt: string
    updatedAt: string
}

export interface RequestTransferData {
    destinationType: TransferDestinationType
    bankAccountId?: number
    partnerId?: number
    professionalId?: number
    supplierId?: number
    pixKey?: string
    pixKeyType?: 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP'
    bankCode?: string
    bankAgency?: string
    bankAccountNumber?: string
    billingId?: number
    amount: number
    description?: string
    scheduledFor?: string
}

const BASE = 'financial/transfers'

export async function getTransfers(params?: {
    status?: FinancialTransferStatus
    kind?: FinancialTransferKind
}): Promise<ApiResponse<FinancialTransfer[]>> {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.kind) qs.set('kind', params.kind)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return apiRequest<FinancialTransfer[]>(`${BASE}${suffix}`, {})
}

export async function getTransfer(id: number): Promise<ApiResponse<FinancialTransfer>> {
    return apiRequest<FinancialTransfer>(`${BASE}/${id}`, {})
}

export async function requestTransfer(data: RequestTransferData): Promise<ApiResponse<FinancialTransfer>> {
    return apiRequest<FinancialTransfer>(BASE, { data }, 'post')
}

export async function approveTransfer(
    id: number,
    password: string,
): Promise<ApiResponse<FinancialTransfer>> {
    return apiRequest<FinancialTransfer>(`${BASE}/${id}/approve`, { data: { password } }, 'post')
}

export async function rejectTransfer(id: number, reason: string): Promise<ApiResponse<FinancialTransfer>> {
    return apiRequest<FinancialTransfer>(`${BASE}/${id}/reject`, { data: { reason } }, 'post')
}

export async function cancelTransfer(
    id: number,
    reason?: string,
): Promise<ApiResponse<FinancialTransfer>> {
    return apiRequest<FinancialTransfer>(`${BASE}/${id}/cancel`, { data: { reason } }, 'post')
}
